import type React from 'react';
import { ToastSeverity } from '../components/General/Toast/Toast';
import { globalStore } from '../Redux/ReduxStore';
import { type IMessageDialogTypes, toastMessages } from '../components/Layout/Dialogs/DialogConfig';
import { contextMenuSlice } from '../Redux/ContextMenuSlice';
import { infoDialogSlice } from '../Redux/InfoDialogSlice';
import { messageDialogSlice } from '../Redux/MessageDialogSlice';
import { stampDialogSlice } from '../Redux/StampDialogSlice';
import { GlobalVariables } from './GlobalVariables';
import type { ILocation, IToastMessage } from '../types';
import { devLog } from '../utils';
import type { OptionsObject, SnackbarKey } from 'notistack';

export type IEnqueueSnackbar = (
    message: string,
    options?: OptionsObject & Partial<{ severity: ToastSeverity; additionalInfo: string }>,
) => SnackbarKey;

export class GlobalDialogs {
    private static _enqueueSnackbar: IEnqueueSnackbar = message => {
        devLog('NotInitialized yet: ', message);
        return -1;
    };

    private static _canShowContextMenu = true;
    private static _debouncingNotyMessages: { [id: string]: boolean } = {};
    private static _dialogContent: React.ReactNode = null;
    private static _isDialogShown = false;
    private static _isContextMenuShown = false;

    static notifications: IToastMessage[] = [];

    static get content(): React.ReactNode {
        return this._dialogContent;
    }

    static get enqueueSnackbar(): IEnqueueSnackbar {
        return this._enqueueSnackbar;
    }

    static set enqueueSnackbar(ctx: IEnqueueSnackbar) {
        this._enqueueSnackbar = ctx;
    }

    static closeAlert() {
        GlobalDialogs.closeMessage();
    }

    static closeDialog() {
        GlobalDialogs._isDialogShown = false;
        GlobalDialogs._canShowContextMenu = true;
        globalStore.dispatch(infoDialogSlice.actions.update(null));
    }

    static closeError() {
        GlobalDialogs.closeMessage();
    }

    static closeInfo() {
        GlobalDialogs.closeMessage();
    }

    static closeLoading() {
        GlobalDialogs.closeMessage();
    }

    static closeMessage() {
        GlobalDialogs._canShowContextMenu = true;
        globalStore.dispatch(messageDialogSlice.actions.update(null));
    }

    static closeStampDialog() {
        globalStore.dispatch(stampDialogSlice.actions.update(false));
    }

    static closeStampDialogs() {
        GlobalDialogs.closeStampDialog();
    }

    static showAlert(title: string, message?: string) {
        GlobalDialogs.showMessage('alert', title, message);
    }

    static showDialog(title: string, content: React.ReactNode) {
        GlobalDialogs._isDialogShown = true;
        GlobalDialogs._canShowContextMenu = false;
        GlobalDialogs._dialogContent = content;

        globalStore.dispatch(
            infoDialogSlice.actions.update({
                title: title,
            }),
        );
    }

    static showError(error: string) {
        GlobalDialogs.showMessage('error', error);
    }

    static showInfo(title: string, message?: string) {
        GlobalDialogs.showMessage('info', title, message);
    }

    static showLoading(message?: string) {
        GlobalDialogs._canShowContextMenu = false;
        GlobalDialogs.showMessage('loading', message ?? '');
    }

    static showStampDialog() {
        globalStore.dispatch(stampDialogSlice.actions.update(true));
    }

    static showContextMenu(anchor: Element, point: ILocation) {
        if (GlobalDialogs._canShowContextMenu) {
            GlobalDialogs._isContextMenuShown = true;
            // Only show context menu if no dialogs are opened.
            GlobalVariables.contextMenuAnchor = anchor;
            globalStore.dispatch(contextMenuSlice.actions.show(point));
        }
    }

    static get isDialogShown(): boolean {
        return GlobalDialogs._isDialogShown;
    }

    static get isContextMenuShown(): boolean {
        return GlobalDialogs._isContextMenuShown;
    }

    static closeContextMenu() {
        GlobalDialogs._isContextMenuShown = false;
        globalStore.dispatch(contextMenuSlice.actions.close());
    }

    static showNotyDebounced(
        ttlSeconds: number,
        message: string,
        type: ToastSeverity,
        manualDismiss = false,
    ) {
        if (this._debouncingNotyMessages[message]) {
            return;
        }
        this._debouncingNotyMessages[message] = true;
        GlobalDialogs.showNoty(message, type, manualDismiss);
        setTimeout(() => {
            this._debouncingNotyMessages[message] = false;
        }, ttlSeconds * 1000.0);
    }

    static showNoty(
        message: string,
        severity: ToastSeverity,
        manualDismiss = false,
        preventDuplicate = false,
    ) {
        if (!message) {
            devLog('Skipping empty Noty message');
            return;
        }

        let duration = 0;
        if (!manualDismiss) {
            duration =
                (severity === ToastSeverity.Error ? toastMessages.error : toastMessages.success) *
                (message.length > 50
                    ? -1.82 + 0.721 * (Math.log(message.length) / Math.log(Math.E))
                    : 1.0);
        }

        const notification = {
            id: Math.floor(Math.random() * 1000).toString(),
            message: message,
            severity: severity,
            dismissAfter: duration,
            dismissible: true,
            preventDuplicate: preventDuplicate,
        };

        // Store notification
        GlobalDialogs.notifications.push(notification);
        GlobalDialogs.showNotifications();
    }

    static showNotifications() {
        // Only show notifications if UI initialized
        if (GlobalDialogs.notifications.length > 0) {
            GlobalDialogs.notifications.forEach(notification => {
                GlobalDialogs.enqueueSnackbar(notification.message, {
                    autoHideDuration: notification.dismissAfter,
                    persist: !notification.dismissible,
                    preventDuplicate: notification.preventDuplicate,
                    severity: notification.severity,
                });
            });
            GlobalDialogs.notifications = [];
        }
    }

    static showMessage(type: IMessageDialogTypes, title: string, message?: string) {
        GlobalDialogs._canShowContextMenu = false;
        globalStore.dispatch(
            messageDialogSlice.actions.update({
                type: type,
                title: title,
                message: message,
            }),
        );
    }
}
